import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { Fragment } from "react";
import { MaterialRead, MixBasket } from "src/store/api/generatedApi";
import {
  DeltaSign,
  emptyMaterialDeltaRow,
  MAX_MATERIAL_DELTAS_PER_CHARGE,
  MaterialDeltaRow,
  plannedMassByMaterial,
  signedMassDelta,
} from "./materialDeltaRows";
import { useDeltaWeightFormat } from "./useDeltaWeightFormat";

interface Props {
  basket: MixBasket;
  rows: MaterialDeltaRow[];
  onRowsChange: (rows: MaterialDeltaRow[]) => void;
  materials: MaterialRead[];
}

export const DeltaMaterialRows = ({
  basket,
  rows,
  onRowsChange,
  materials,
}: Props) => {
  const { t } = useTenantTranslation();
  const { massScaleFactor, unitLabel, weightInputStep, formatWeight } =
    useDeltaWeightFormat();
  const alphabeticalMaterials = [...materials].sort((left, right) =>
    left.name.localeCompare(right.name)
  );
  const plannedMass = plannedMassByMaterial(basket);
  const massDeltas = rows.map(
    (row) => signedMassDelta({ row, massScaleFactor }) ?? 0
  );
  const chargeTotal =
    [...plannedMass.values()].reduce((total, mass) => total + mass, 0) +
    massDeltas.reduce((total, massDelta) => total + massDelta, 0);

  const updateRow = (rowKey: string, change: Partial<MaterialDeltaRow>) => {
    onRowsChange(
      rows.map((row) => (row.rowKey === rowKey ? { ...row, ...change } : row))
    );
  };

  return (
    <Stack sx={{ gap: rows.length > 0 ? 1.5 : 0 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "minmax(140px, 1fr) auto max-content max-content",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {rows.map((row, index) => {
          const chosenElsewhere = new Set(
            rows
              .filter((other) => other.rowKey !== row.rowKey)
              .map((other) => other.materialName)
          );
          const materialTotal =
            (row.materialName === null
              ? 0
              : (plannedMass.get(row.materialName) ?? 0)) +
            (massDeltas[index] ?? 0);
          const isWeightInvalid =
            signedMassDelta({ row, massScaleFactor }) === null ||
            materialTotal < 0;
          return (
            <Fragment key={row.rowKey}>
              <TextField
                select
                size="small"
                value={row.materialName ?? ""}
                error={row.materialName === null}
                onChange={(event) => {
                  updateRow(row.rowKey, { materialName: event.target.value });
                }}
                slotProps={{
                  select: { displayEmpty: true },
                  htmlInput: { "aria-label": t("material") },
                }}
                fullWidth
              >
                <MenuItem value="" disabled>
                  {t("selectMaterial")}
                </MenuItem>
                {alphabeticalMaterials
                  .filter((material) => !chosenElsewhere.has(material.name))
                  .map((material) => (
                    <MenuItem key={material.id} value={material.name}>
                      {material.name}
                    </MenuItem>
                  ))}
              </TextField>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={row.sign}
                onChange={(_event, value: DeltaSign | null) => {
                  if (value !== null) {
                    updateRow(row.rowKey, { sign: value });
                  }
                }}
              >
                <ToggleButton value="plus">{"+"}</ToggleButton>
                <ToggleButton value="minus">{"−"}</ToggleButton>
              </ToggleButtonGroup>
              <Stack direction="row" sx={{ alignItems: "center" }}>
                <TextField
                  size="small"
                  type="number"
                  value={row.weightInput}
                  error={isWeightInvalid}
                  onChange={(event) => {
                    updateRow(row.rowKey, { weightInput: event.target.value });
                  }}
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: weightInputStep,
                      sx: { width: "8ch" },
                    },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {t("deltaWeightPerHeat", { unit: unitLabel })}
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <IconButton
                  aria-label={t("delete")}
                  onClick={() => {
                    onRowsChange(
                      rows.filter((other) => other.rowKey !== row.rowKey)
                    );
                  }}
                >
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  width: "22ch",
                  color: materialTotal < 0 ? "error.main" : "text.secondary",
                }}
              >
                {row.materialName !== null &&
                  t("deltaNewTotal", { weight: formatWeight(materialTotal) })}
              </Typography>
            </Fragment>
          );
        })}
      </Box>
      <Stack sx={{ alignItems: "flex-start", gap: 1 }}>
        <Button
          startIcon={<AddRoundedIcon />}
          disabled={rows.length >= MAX_MATERIAL_DELTAS_PER_CHARGE}
          onClick={() => {
            onRowsChange([...rows, emptyMaterialDeltaRow()]);
          }}
        >
          {t("deltaAddChargeAdjustment", { charge: basket.basket_number })}
        </Button>
        {rows.length > 0 && (
          <Typography variant="body2">
            {t("deltaNewChargeTotal", { weight: formatWeight(chargeTotal) })}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
